(() => {
  const ROOT_SELECTOR = '[data-ecomhard-traje]';

  const formatMoney = (cents, moneyFormat) => {
    const value = Number(cents || 0);
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try {
        return window.Shopify.formatMoney(value, moneyFormat);
      } catch (error) {
        // Fallback abaixo.
      }
    }

    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value / 100);
  };

  const sameOptions = (a, b) => {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((value, index) => String(value) === String(b[index]));
  };

  class EcomhardTrajeCompleto {
    constructor(root) {
      if (!root || root.dataset.ecomhardTrajeReady === 'true') return;

      root.dataset.ecomhardTrajeReady = 'true';
      this.root = root;
      this.moneyFormat = root.dataset.moneyFormat || 'R$ {{amount_with_comma_separator}}';
      this.pixDiscount = Math.max(0, Number(root.dataset.pixDiscount || 0));
      this.installmentsMax = Math.max(1, Number(root.dataset.installmentsMax || 1));
      this.installmentMinCents = Math.max(0, Number(root.dataset.installmentMinCents || 0));
      this.cards = Array.from(root.querySelectorAll('[data-outfit-product]'));
      this.addButton = root.querySelector('[data-outfit-add-all]');
      this.addLabel = root.querySelector('[data-outfit-add-label]');
      this.message = root.querySelector('[data-outfit-message]');
      this.defaultButtonText = this.addLabel?.textContent?.trim() || 'ADICIONAR TUDO AO CARRINHO';

      this.totalBase = root.querySelector('[data-outfit-total]');
      this.totalCompare = root.querySelector('[data-outfit-total-compare]');
      this.totalInstallments = root.querySelector('[data-outfit-total-installments]');
      this.totalPix = root.querySelector('[data-outfit-total-pix]');
      this.totalPixValue = root.querySelector('[data-outfit-total-pix-value]');
      this.lookMedia = root.querySelector('.ecomhard-traje__look-media');
      this.productsPanel = root.querySelector('.ecomhard-traje__products-panel');
      this.productsScroller = root.querySelector('[data-outfit-products]');

      this.syncPanelHeight();
      if (this.lookMedia && 'ResizeObserver' in window) {
        this.panelResizeObserver = new ResizeObserver(() => this.syncPanelHeight());
        this.panelResizeObserver.observe(this.lookMedia);
      } else if (this.lookMedia) {
        this._boundPanelResize = () => this.syncPanelHeight();
        window.addEventListener('resize', this._boundPanelResize, { passive: true });
      }

      this.cards.forEach((card) => this.initCard(card));
      this.addButton?.addEventListener('click', () => this.addSelectedToCart());
      this.updateTotals();

      /* Garante uma segunda sincronização após o layout inicial e carregamento das imagens. */
      requestAnimationFrame(() => {
        this.syncPanelHeight();
        requestAnimationFrame(() => this.syncPanelHeight());
      });
    }

    syncPanelHeight() {
      if (!this.productsPanel) return;

      const isMobile = window.matchMedia('(max-width: 749px)').matches;

      if (isMobile) {
        /* No mobile o carrossel mostra 1 item inteiro + ~22% do próximo.
           Medimos os cards já renderizados para não depender de uma altura fixa. */
        this.root.style.removeProperty('--ecomhard-traje-panel-height');
        this.root.style.removeProperty('--ecomhard-traje-mobile-card-height');
        this.root.style.removeProperty('--ecomhard-traje-mobile-products-height');

        const productCount = this.cards?.length || 0;
        if (!productCount) return;

        requestAnimationFrame(() => {
          const heights = this.cards.map((card) => {
            const rect = card.getBoundingClientRect();
            return Math.ceil(Math.max(rect.height, card.scrollHeight || 0));
          });
          const cardHeight = Math.max(1, ...heights);

          this.root.style.setProperty('--ecomhard-traje-mobile-card-height', `${cardHeight}px`);

          if (productCount > 1) {
            const peekRatio = 0.22;
            const viewportHeight = Math.round(cardHeight * (1 + peekRatio));
            this.root.style.setProperty('--ecomhard-traje-mobile-products-height', `${viewportHeight}px`);
          }
        });
        return;
      }

      this.root.style.removeProperty('--ecomhard-traje-mobile-card-height');
      this.root.style.removeProperty('--ecomhard-traje-mobile-products-height');

      if (!this.lookMedia) {
        this.root.style.removeProperty('--ecomhard-traje-panel-height');
        return;
      }

      const height = Math.round(this.lookMedia.getBoundingClientRect().height);
      if (height > 0) {
        this.root.style.setProperty('--ecomhard-traje-panel-height', `${height}px`);
      }
    }

    initCard(card) {
      const variantsScript = card.querySelector('[data-outfit-variants]');
      let variants = [];

      try {
        variants = JSON.parse(variantsScript?.textContent || '[]');
      } catch (error) {
        console.error('[REV][Traje Completo] Erro ao ler variantes:', error);
      }

      card._ecomhardVariants = variants;
      card._ecomhardRequiresVariantSelection = card.dataset.requiresVariantSelection === 'true';

      const initialVariant = variants.find((variant) => String(variant.id) === String(card.dataset.initialVariantId)) || variants.find((variant) => variant.available) || variants[0] || null;
      card._ecomhardDisplayVariant = initialVariant;
      card._ecomhardVariant = card._ecomhardRequiresVariantSelection ? null : initialVariant;

      const optionButtons = Array.from(card.querySelectorAll('[data-outfit-option-value]'));
      const checkbox = card.querySelector('[data-outfit-checkbox]');

      optionButtons.forEach((button) => {
        button.addEventListener('click', () => {
          if (button.getAttribute('aria-disabled') === 'true' || button.classList.contains('is-unavailable')) return;
          this.resolveVariant(card, button);
        });
      });

      checkbox?.addEventListener('change', () => this.updateTotals());

      if (card._ecomhardRequiresVariantSelection) {
        if (checkbox) {
          checkbox.checked = false;
          checkbox.disabled = true;
        }
      } else {
        this.applyVariant(card, card._ecomhardVariant, false, false);
      }
    }

    selectedOptions(card) {
      const groups = Array.from(card.querySelectorAll('[data-outfit-option-group]'));
      return groups
        .sort((a, b) => Number(a.dataset.optionIndex || 0) - Number(b.dataset.optionIndex || 0))
        .map((group) => group.querySelector('[data-outfit-option-value].is-selected')?.dataset.value || '');
    }

    syncOptionButtons(card, variant) {
      if (!variant?.options) return;

      const buttons = Array.from(card.querySelectorAll('[data-outfit-option-value]'));
      buttons.forEach((button) => {
        const optionIndex = Number(button.dataset.optionIndex || 0);
        const selected = String(variant.options[optionIndex]) === String(button.dataset.value);
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
    }

    resolveVariant(card, changedButton) {
      const variants = card._ecomhardVariants || [];
      const group = changedButton.closest('[data-outfit-option-group]');

      group?.querySelectorAll('[data-outfit-option-value]').forEach((button) => {
        const selected = button === changedButton;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });

      const selectedOptions = this.selectedOptions(card);
      const hasAllOptions = selectedOptions.length > 0 && selectedOptions.every((value) => value !== '');

      /* Não escolhe automaticamente as demais opções: só existe variante ativa quando
         o cliente completar todos os seletores. */
      if (!hasAllOptions) {
        card._ecomhardVariant = null;
        const checkbox = card.querySelector('[data-outfit-checkbox]');
        if (checkbox) {
          checkbox.checked = false;
          checkbox.disabled = true;
        }
        card.querySelector('[data-outfit-unavailable]')?.classList.add('is-hidden');
        this.updateTotals();
        return;
      }

      const variant = variants.find((item) => sameOptions(item.options, selectedOptions)) || null;
      this.applyVariant(card, variant, true, true);
    }

    applyVariant(card, variant, updateTotals = true, autoSelectProduct = false) {
      card._ecomhardVariant = variant;

      const checkbox = card.querySelector('[data-outfit-checkbox]');
      const price = card.querySelector('[data-outfit-price]');
      const compare = card.querySelector('[data-outfit-compare]');
      const unavailable = card.querySelector('[data-outfit-unavailable]');

      if (!variant) {
        if (checkbox) {
          checkbox.checked = false;
          checkbox.disabled = true;
        }
        unavailable?.classList.remove('is-hidden');
        if (updateTotals) this.updateTotals();
        return;
      }

      this.syncOptionButtons(card, variant);

      if (price) price.textContent = formatMoney(variant.price, this.moneyFormat);

      if (compare) {
        const showCompare = Number(variant.compareAtPrice || 0) > Number(variant.price || 0);
        compare.textContent = showCompare ? formatMoney(variant.compareAtPrice, this.moneyFormat) : '';
        compare.classList.toggle('is-hidden', !showCompare);
      }

      if (checkbox) {
        checkbox.disabled = !variant.available;
        if (!variant.available) {
          checkbox.checked = false;
        } else if (autoSelectProduct) {
          /* Ao escolher uma variante válida, o produto entra automaticamente na seleção. */
          checkbox.checked = true;
        }
      }

      unavailable?.classList.toggle('is-hidden', Boolean(variant.available));
      if (updateTotals) this.updateTotals();
    }

    selectedItems() {
      return this.cards
        .filter((card) => card.querySelector('[data-outfit-checkbox]')?.checked)
        .map((card) => ({ card, variant: card._ecomhardVariant }))
        .filter(({ variant }) => variant?.available);
    }

    installmentCount(total) {
      let count = this.installmentsMax;
      if (this.installmentMinCents > 0) {
        const allowed = Math.floor(total / this.installmentMinCents);
        count = Math.min(count, Math.max(1, allowed));
      }
      return Math.max(1, count);
    }

    updateTotals() {
      const selected = this.selectedItems();
      let total = 0;
      let compareBaseline = 0;
      let hasCompare = false;

      selected.forEach(({ variant }) => {
        const price = Number(variant.price || 0);
        const compareAt = Number(variant.compareAtPrice || 0);
        total += price;

        if (compareAt > price) {
          compareBaseline += compareAt;
          hasCompare = true;
        } else {
          compareBaseline += price;
        }
      });

      if (this.totalBase) this.totalBase.textContent = formatMoney(total, this.moneyFormat);

      if (this.totalCompare) {
        const showCompare = hasCompare && compareBaseline > total;
        this.totalCompare.textContent = showCompare ? formatMoney(compareBaseline, this.moneyFormat) : '';
        this.totalCompare.classList.toggle('is-hidden', !showCompare);
      }

      if (this.totalInstallments) {
        const showInstallments = total > 0;
        this.totalInstallments.textContent = showInstallments ? `${this.installmentCount(total)}x sem juros` : '';
        this.totalInstallments.classList.toggle('is-hidden', !showInstallments);
      }

      if (this.totalPix && this.totalPixValue) {
        const showPix = total > 0 && this.pixDiscount > 0;
        const pixTotal = Math.floor(total * ((100 - this.pixDiscount) / 100));
        this.totalPixValue.textContent = showPix ? formatMoney(pixTotal, this.moneyFormat) : '';
        this.totalPix.classList.toggle('is-hidden', !showPix);
      }

      const canAdd = selected.length > 0;
      const hasSelectedVariant = this.cards.some((card) => {
        if (!card._ecomhardRequiresVariantSelection) return false;
        return Boolean(card._ecomhardVariant?.available);
      });

      if (this.addButton) {
        this.addButton.disabled = !canAdd;
        this.addButton.setAttribute('aria-disabled', canAdd ? 'false' : 'true');
      }

      if (this.addLabel && !this.addButton?.classList.contains('is-loading')) {
        this.addLabel.textContent = !canAdd && !hasSelectedVariant
          ? 'Selecione uma variante'
          : this.defaultButtonText;
      }

      if (this.message && canAdd) this.message.textContent = '';
    }

    async addSelectedToCart() {
      const selected = this.selectedItems();
      if (!selected.length || !this.addButton) return;

      const items = selected.map(({ variant }) => ({ id: Number(variant.id), quantity: 1 }));
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const endpoint = window.routes?.cart_add_url || '/cart/add.js';

      this.addButton.disabled = true;
      this.addButton.setAttribute('aria-disabled', 'true');
      this.addButton.classList.add('is-loading');
      if (this.addLabel) this.addLabel.textContent = 'ADICIONANDO...';
      if (this.message) this.message.textContent = '';

      const payload = { items };

      if (cart && typeof cart.getSectionsToRender === 'function') {
        payload.sections = cart.getSectionsToRender().map((section) => section.section);
        payload.sections_url = window.location.pathname;
        if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
      }

      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'application/json'
          },
          body: JSON.stringify(payload)
        });

        const data = await response.json();
        if (!response.ok || data.status || data.errors) {
          throw new Error(data.description || data.message || data.errors || 'Não foi possível adicionar os produtos.');
        }

        if (cart && typeof cart.renderContents === 'function' && data.sections) {
          cart.renderContents(data);
        } else if (!cart) {
          window.location.href = window.routes?.cart_url || '/cart';
          return;
        }

        if (typeof window.updateCartCounters === 'function') window.updateCartCounters();

        this.cards.forEach((card) => {
          const checkbox = card.querySelector('[data-outfit-checkbox]');
          if (checkbox) checkbox.checked = false;
        });

        this.updateTotals();
        if (this.message) this.message.textContent = 'Produtos adicionados ao carrinho.';
      } catch (error) {
        console.error('[REV][Traje Completo]', error);
        if (this.message) this.message.textContent = error?.message || 'Não foi possível adicionar os produtos. Tente novamente.';
      } finally {
        this.addButton.classList.remove('is-loading');
        if (this.addLabel) this.addLabel.textContent = this.defaultButtonText;
        this.updateTotals();
      }
    }
  }

  const init = (scope = document) => {
    const roots = scope.matches?.(ROOT_SELECTOR)
      ? [scope]
      : Array.from(scope.querySelectorAll?.(ROOT_SELECTOR) || []);

    roots.forEach((root) => new EcomhardTrajeCompleto(root));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
